import { type AnimationPlaybackControls } from 'motion/react';

export class AnimationScopeCancelled extends Error {
  constructor() {
    super('animation scope cancelled');
  }
}

export type AnimationScope = {
  track: (controls: AnimationPlaybackControls) => Promise<void>;
  rest: (ms: number) => Promise<void>;
  cancel: () => void;
};

const markHandled = <T>(promise: Promise<T>) => {
  promise.catch(() => undefined);
  return promise;
};

export const createAnimationScope = (): AnimationScope => {
  const running = new Set<AnimationPlaybackControls>();
  const timers = new Set<ReturnType<typeof setTimeout>>();
  let cancelled = false;
  let rejectCancellation = () => {};
  const cancellation = markHandled(
    new Promise<never>((_, reject) => {
      rejectCancellation = () => reject(new AnimationScopeCancelled());
    })
  );

  const track = (controls: AnimationPlaybackControls) => {
    if (cancelled) {
      controls.stop();
      return cancellation;
    }
    running.add(controls);
    return markHandled(
      Promise.race([controls.finished, cancellation]).then(
        () => {
          running.delete(controls);
        },
        (error: unknown) => {
          running.delete(controls);
          throw error;
        }
      )
    );
  };

  const rest = (ms: number) =>
    Promise.race([
      new Promise<void>((resolve) => {
        const timer = setTimeout(() => {
          timers.delete(timer);
          resolve();
        }, ms);
        timers.add(timer);
      }),
      cancellation,
    ]);

  const cancel = () => {
    cancelled = true;
    rejectCancellation();
    timers.forEach(clearTimeout);
    timers.clear();
    running.forEach((controls) => controls.stop());
    running.clear();
  };

  return { track, rest, cancel };
};

export const ignoreCancellation = (error: unknown) => {
  if (!(error instanceof AnimationScopeCancelled)) throw error;
};
